// Application controller: the connection to the game server (guest identity,
// lobby, rooms), map bundles (downloaded from the server, cached in
// IndexedDB), map loading, the game session and settings. It owns no menu
// DOM: it publishes its state as signals (ui/state.ts) and the Preact screens
// call its actions.

import { effect } from '@preact/signals';
import * as THREE from 'three';
import { GameAudio } from './audio/gameAudio';
import { SoundSystem } from './audio/soundSystem';
import { gpuInfo, type GpuInfo } from './render/gpuInfo';
import { Pk3Archive } from './fs/pk3';
import { storedBundles, storedThumbs } from './fs/storage';
import { VirtualFS } from './fs/vfs';
import { Game, type GameUi, type RunResult } from './game/game';
import { Input } from './game/input';
import { listMaps, loadMap, loadShaders } from './game/mapLoader';
import { NetClient, type RoomJoined } from './game/net';
import { PlayerAssets } from './game/playerAssets';
import { pushRecent, type Settings } from './game/settings';
import { TEAM_NAMES, type Team } from './shared/game/mapLogic';
import { TextureManager } from './render/textures';
import { loadArenas, type ArenaInfo } from './shared/arena';
import { crc32, type AdminStatus, type CreateRoomOptions, type MapInfo, type ServerMessage } from './shared/net/protocol';
import { Hud } from './ui/hud';
import { el } from './ui/hud/dom';
import { clearLevelshotCache, findLevelshot } from './ui/maps/levelshots';
import { indexMaps } from './ui/maps/mapMeta';
import {
  game as gameState,
  loading,
  mapIndexVersion,
  net as netState,
  pauseView,
  phase,
  roomPlayers,
  runResult,
  runResultVisible,
  scoreboardOpen,
  selectedMap,
  settings,
  settingsStore,
  spectating,
  tab,
  toast,
  updateSettings,
  vfsVersion,
} from './ui/state';

/** Maps that ship with the client (playable offline). */
const BUILTIN_PK3 = ['tjtest.pk3'];
/** Player model bundles are named player-<crc>.pk3, sound bundles sound-<crc>.pk3 (server/maps.ts). */
const PLAYER_BUNDLE = /^player-[0-9a-f]+\.pk3$/;
const SOUND_BUNDLE = /^sound-[0-9a-f]+\.pk3$/;
/** The official server: the page's own host in production, the dev server port while developing. */
export const SERVER_URL: string =
  (import.meta.env.VITE_SERVER_URL as string | undefined) ||
  (import.meta.env.DEV ? `ws://${location.hostname || 'localhost'}:27961` : `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`);
const IDENTITY_KEY = 'tjgame.identity';
const PING_INTERVAL_MS = 5000;
/** Waits between reconnection attempts (the last one repeats). */
const RETRY_DELAYS_MS = [2000, 5000, 10000, 30000];
/** How long resume() waits for the pointer lock before falling back to the pause menu. */
const LOCK_TIMEOUT_MS = 800;
/** The run result panel hides itself after this long. */
const RESULT_PANEL_MS = 8000;

/** Rough share of the loading bar for each loadMap stage. */
const LOAD_STAGES: [RegExp, number][] = [
  [/^Parsing shader/, 0.55],
  [/^Loading maps\//, 0.6],
  [/^Building collision/, 0.7],
  [/^Building geometry/, 0.8],
];

function storedToken(): string | null {
  try {
    return (JSON.parse(localStorage.getItem(IDENTITY_KEY) ?? '{}') as { token?: string }).token ?? null;
  } catch {
    return null;
  }
}

export class App {
  readonly canvas: HTMLCanvasElement;
  /** Container the Preact UI is rendered into (above the canvas and HUD). */
  readonly uiRoot: HTMLDivElement;
  readonly input: Input;
  /** Maps available on this device: built-in ones and downloaded bundles. */
  readonly vfs = new VirtualFS();
  private readonly renderer: THREE.WebGLRenderer;
  private readonly hud: Hud;
  /** Textures of the loaded map (disposed with it). */
  private textures: TextureManager | null = null;
  /** The player model, loaded from its bundle once (kept across maps), and its textures. */
  private players: { bundle: string; assets: PlayerAssets; textures: TextureManager } | null = null;
  /** The player sounds bundle (kept across maps). */
  private soundBundle: Pk3Archive | null = null;
  /** Sound output, shared by every map. */
  readonly sound = new SoundSystem();
  /** The GPU (Settings shows it; weak ones get the low preset). */
  readonly gpu: GpuInfo;
  private game: Game | null = null;
  private busy = false;
  private arenaInfo = new Map<string, ArenaInfo>();
  private stopIndexing: (() => void) | null = null;
  private pingTimer = 0;
  private retryTimer = 0;
  private retries = 0;
  private resultTimer = 0;
  private offNet: (() => void) | null = null;
  /** Escape that released the pointer lock is also delivered to the page in some browsers. */
  private pausedAt = 0;
  /** The player joined a team on the loaded map (play()). */
  private teamJoined = false;

  constructor(root: HTMLElement) {
    this.canvas = el('canvas', 'game-canvas');
    root.appendChild(this.canvas);
    // antialiasing is fixed when the context is made: the low preset turns it off from the next start
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: !settings.peek().lowGraphics, powerPreference: 'high-performance' });
    this.gpu = gpuInfo(this.renderer.getContext());
    if (!settings.peek().gpuChecked) {
      // the first start: a weak GPU gets the low preset (it can be switched off in Settings -> Video)
      const weak = this.gpu.weakReasons.length > 0;
      updateSettings({ gpuChecked: true, ...(weak ? { lowGraphics: true } : {}) });
      if (weak) setTimeout(() => toast(`Low graphics preset on: ${this.gpu.weakReasons.join(', ')}`, 'info', 8000), 1500);
    }
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.hud = new Hud(root);
    this.hud.setVisible(false);
    this.input = new Input(this.canvas);
    this.uiRoot = el('div', 'ui-root');
    root.appendChild(this.uiRoot);

    window.addEventListener('resize', this.onResize);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    this.canvas.addEventListener('click', () => {
      if (this.game && !this.input.locked && phase.peek() === 'playing') this.resume();
    });
    if (import.meta.env.DEV) (window as unknown as { __tjgame: App }).__tjgame = this;
    effect(() => this.applySettings(settings.value));
    // the HUD shows while a map is loaded and not behind the loading screen
    effect(() => {
      const p = phase.value;
      this.hud.setVisible(!!this.game && (p === 'playing' || p === 'paused'));
    });
  }

  async init(): Promise<void> {
    for (const name of BUILTIN_PK3) {
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}baseq/${name}`);
        if (res.ok) await this.vfs.mountBlob(await res.blob(), name);
      } catch (err) {
        console.warn(`builtin ${name}:`, err);
      }
    }
    try {
      for (const name of await storedBundles.names()) {
        if (PLAYER_BUNDLE.test(name) || SOUND_BUNDLE.test(name)) continue;
        const blob = await storedBundles.load(name);
        if (blob) await this.vfs.mountBlob(blob, name);
      }
    } catch (err) {
      console.warn('IndexedDB unavailable:', err);
    }
    this.vfsChanged();
    phase.value = 'menu';
    this.connect();
  }

  // --- settings ---

  private applySettings(s: Settings): void {
    const html = document.documentElement;
    html.style.setProperty('--ui-scale', String(s.uiScale));
    html.classList.toggle('reduce-motion', s.reduceMotion);
    html.classList.toggle('high-contrast', s.highContrast);
    this.input.binds = s.binds;
    this.input.rawInput = s.rawInput;
    this.sound.setVolumes({ master: s.volume, effects: s.effectsVolume, ambient: s.ambientVolume });
    this.hud.configure(s.hud, { showFps: s.showFps, largeText: s.largeHudText });
    // low preset: no high-DPI rendering and 80 % of the render scale
    const ratio = s.lowGraphics ? s.renderScale * 0.8 : Math.min(window.devicePixelRatio, 2) * s.renderScale;
    if (Math.abs(this.renderer.getPixelRatio() - ratio) > 1e-6) {
      this.renderer.setPixelRatio(ratio);
      this.onResize();
    }
    this.game?.applySettings();
  }

  /** Anisotropic filtering of new textures (off in the low preset). */
  private get anisotropy(): number {
    return settings.peek().lowGraphics ? 1 : this.renderer.capabilities.getMaxAnisotropy();
  }

  /** Physics changes end the current run; asks first when a run is being timed. */
  get runActive(): boolean {
    return !!this.game?.map.session.timerun;
  }

  setPhysicsMsec(msec: number): void {
    this.game?.map.session.interrupt('physics changed');
    updateSettings({ physicsMsec: msec });
  }

  /** Changes the player name (on the server too, where names are reserved). */
  async setName(name: string): Promise<boolean> {
    name = name.trim();
    if (!name) return false;
    const client = netState.peek().client;
    try {
      const accepted = client ? await client.setName(name) : name;
      updateSettings({ playerName: accepted });
      return true;
    } catch (err) {
      toast((err as Error).message, 'bad');
      return false;
    }
  }

  // --- maps on this device ---

  get arenas(): ReadonlyMap<string, ArenaInfo> {
    return this.arenaInfo;
  }

  /** Maps that can be loaded without downloading. */
  maps(): string[] {
    return listMaps(this.vfs);
  }

  /** Refreshes everything derived from the mounted maps. */
  private vfsChanged(): void {
    clearLevelshotCache();
    vfsVersion.value++;
    loadArenas(this.vfs)
      .then((a) => {
        this.arenaInfo = a;
        mapIndexVersion.value++;
      })
      .catch((err) => console.warn('arenas:', err));
    this.stopIndexing?.();
    this.stopIndexing = indexMaps(this.vfs, this.maps(), () => mapIndexVersion.value++);
  }

  private async localChecksum(map: string): Promise<number> {
    const data = await this.vfs.read(`maps/${map}.bsp`);
    return data ? crc32(data) : -1;
  }

  /** Makes sure the server's version of a map is mounted: from the cache or downloaded. */
  private async ensureBundle(client: NetClient, info: MapInfo): Promise<void> {
    // a built-in copy of the map has the same BSP but not what the server adds (sounds)
    const builtin = BUILTIN_PK3.includes(this.vfs.archiveOf(`maps/${info.name}.bsp`)?.name ?? '');
    if (!builtin && (await this.localChecksum(info.name)) === info.checksum) return;
    let blob = await storedBundles.load(info.bundle).catch(() => undefined);
    if (!blob) {
      const mb = (info.bundleSize / 1048576).toFixed(1);
      loading.value = { map: info.name, stage: `Downloading map (${mb} MB)`, progress: 0.02 };
      const got = await client.downloadWithProgress(`/bundle/${encodeURIComponent(info.bundle)}`, info.bundleSize, (f) => {
        loading.value = { map: info.name, stage: `Downloading map (${mb} MB)`, progress: 0.02 + f * 0.48 };
      });
      if (!got) throw new Error('the server could not send the map');
      blob = got;
      await storedBundles.save(info.bundle, blob).catch((err) => console.warn('could not cache the map', err));
      // older versions of this map are not needed any more
      for (const name of await storedBundles.names().catch(() => [] as string[])) {
        if (name !== info.bundle && name.startsWith(`${info.name}-`)) await storedBundles.remove(name).catch(() => undefined);
      }
    }
    for (const a of [...this.vfs.mounted]) if ((a.name.startsWith(`${info.name}-`) || a.name === `${info.name}.pk3`) && a.name !== info.bundle) this.vfs.unmount(a.name);
    await this.vfs.mountBlob(blob, info.bundle);
    this.vfsChanged();
    if ((await this.localChecksum(info.name)) !== info.checksum) throw new Error('the downloaded map does not match the server');
  }

  /** Loads the player model from a bundle (once per bundle); failures leave the boxes. */
  private async loadPlayerAssets(name: string, blob: Blob): Promise<void> {
    if (this.players?.bundle === name) return;
    const archive = await Pk3Archive.open(blob, name);
    const assets = await PlayerAssets.load(VirtualFS.of([archive]));
    this.players?.textures.dispose();
    this.players = { bundle: name, assets, textures: new TextureManager(VirtualFS.of([archive]), this.anisotropy) };
  }

  /** The server's player model bundle: from the cache or downloaded (the old versions are removed). */
  private async ensurePlayerAssets(client: NetClient, map: string): Promise<void> {
    const info = client.assets.find((a) => a.kind === 'player');
    if (!info || this.players?.bundle === info.bundle) return;
    try {
      let blob = await storedBundles.load(info.bundle).catch(() => undefined);
      if (!blob) {
        const mb = (info.size / 1048576).toFixed(1);
        loading.value = { map, stage: `Downloading player model (${mb} MB)`, progress: 0.02 };
        const got = await client.downloadWithProgress(`/bundle/${encodeURIComponent(info.bundle)}`, info.size, (f) => {
          loading.value = { map, stage: `Downloading player model (${mb} MB)`, progress: 0.02 + f * 0.2 };
        });
        if (!got) throw new Error('the server could not send it');
        blob = got;
        await storedBundles.save(info.bundle, blob).catch((err) => console.warn('could not cache the player model', err));
        for (const name of await storedBundles.names().catch(() => [] as string[])) {
          if (name !== info.bundle && PLAYER_BUNDLE.test(name)) await storedBundles.remove(name).catch(() => undefined);
        }
      }
      await this.loadPlayerAssets(info.bundle, blob);
    } catch (err) {
      console.warn('player model:', err);
      toast(`Player model not available: ${(err as Error).message}`, 'bad');
    }
  }

  /** The server's player sounds: from the cache or downloaded (a game without them is silent). */
  private async ensureSoundAssets(client: NetClient, map: string): Promise<void> {
    const info = client.assets.find((a) => a.kind === 'sound');
    if (!info || this.soundBundle?.name === info.bundle) return;
    try {
      let blob = await storedBundles.load(info.bundle).catch(() => undefined);
      if (!blob) {
        const mb = (info.size / 1048576).toFixed(1);
        loading.value = { map, stage: `Downloading sounds (${mb} MB)`, progress: 0.02 };
        const got = await client.downloadWithProgress(`/bundle/${encodeURIComponent(info.bundle)}`, info.size, (f) => {
          loading.value = { map, stage: `Downloading sounds (${mb} MB)`, progress: 0.02 + f * 0.1 };
        });
        if (!got) throw new Error('the server could not send them');
        blob = got;
        await storedBundles.save(info.bundle, blob).catch((err) => console.warn('could not cache the sounds', err));
        for (const name of await storedBundles.names().catch(() => [] as string[])) {
          if (name !== info.bundle && SOUND_BUNDLE.test(name)) await storedBundles.remove(name).catch(() => undefined);
        }
      }
      this.soundBundle = await Pk3Archive.open(blob, info.bundle);
    } catch (err) {
      console.warn('sounds:', err);
    }
  }

  /** Offline: the player sounds from the cache, if they were downloaded before. */
  private async cachedSoundAssets(): Promise<void> {
    if (this.soundBundle) return;
    try {
      const name = (await storedBundles.names()).find((n) => SOUND_BUNDLE.test(n));
      const blob = name ? await storedBundles.load(name) : undefined;
      if (name && blob) this.soundBundle = await Pk3Archive.open(blob, name);
    } catch (err) {
      console.warn('sounds:', err);
    }
  }

  /** Offline: the player model from the cache, if it was downloaded before. */
  private async cachedPlayerAssets(): Promise<void> {
    if (this.players) return;
    try {
      const name = (await storedBundles.names()).find((n) => PLAYER_BUNDLE.test(n));
      const blob = name ? await storedBundles.load(name) : undefined;
      if (name && blob) await this.loadPlayerAssets(name, blob);
    } catch (err) {
      console.warn('player model:', err);
    }
  }

  // --- server connection ---

  connect(): void {
    if (netState.peek().status !== 'offline') return;
    clearTimeout(this.retryTimer);
    netState.value = { ...netState.peek(), status: 'connecting', error: null, retryAt: null };
    NetClient.connect(SERVER_URL, settings.peek().playerName, storedToken()).then(
      (client) => this.onConnected(client),
      (err: Error) => {
        netState.value = { ...netState.peek(), status: 'offline', client: null, error: err.message };
        this.scheduleRetry();
      },
    );
  }

  private scheduleRetry(): void {
    clearTimeout(this.retryTimer);
    const delay = RETRY_DELAYS_MS[Math.min(this.retries++, RETRY_DELAYS_MS.length - 1)];
    netState.value = { ...netState.peek(), retryAt: Date.now() + delay };
    this.retryTimer = window.setTimeout(() => this.connect(), delay);
  }

  private onConnected(client: NetClient): void {
    this.retries = 0;
    if (client.newToken) localStorage.setItem(IDENTITY_KEY, JSON.stringify({ token: client.newToken }));
    if (client.name !== settings.peek().playerName) updateSettings({ playerName: client.name });
    client.onClose = (reason) => this.onDisconnected(reason);
    this.offNet = client.on(this.onServerMessage);
    netState.value = { status: 'online', client, serverName: client.serverName, maps: client.maps, rooms: [], room: null, ping: null, error: null, retryAt: null };
    client.lobby(true);
    this.measurePing();
    this.pingTimer = window.setInterval(() => this.measurePing(), PING_INTERVAL_MS);
  }

  private onDisconnected(reason: string): void {
    clearInterval(this.pingTimer);
    this.offNet?.();
    this.offNet = null;
    netState.value = { ...netState.peek(), status: 'offline', client: null, rooms: [], room: null, ping: null, error: reason };
    if (this.game?.net) {
      this.leaveMap();
      toast(`Disconnected from the server: ${reason}`, 'bad', 7000);
    } else toast(`Server connection lost: ${reason}`, 'bad');
    this.scheduleRetry();
  }

  private measurePing(): void {
    const client = netState.peek().client;
    if (!client) return;
    client.ping().then(
      (ms) => {
        if (netState.peek().client === client) netState.value = { ...netState.peek(), ping: Math.round(ms) };
      },
      () => {
        if (netState.peek().client === client) netState.value = { ...netState.peek(), ping: null };
      },
    );
  }

  /** The server's map list again (players). */
  refreshMaps(): void {
    const client = netState.peek().client;
    client?.getJson<MapInfo[]>('/maps').then(
      (maps) => {
        if (maps && netState.peek().client === client) {
          client.maps = maps;
          netState.value = { ...netState.peek(), maps };
        }
      },
      () => undefined,
    );
  }

  /** The admin panel's requests, with this player's token. */
  private admin<T>(path: string, init: RequestInit = {}): Promise<T> {
    const client = netState.peek().client;
    const token = storedToken();
    if (!client || !token) return Promise.reject(new Error('Not connected to the server'));
    return client.adminRequest<T>(path, token, init);
  }

  adminStatus(): Promise<AdminStatus> {
    return this.admin<AdminStatus>('/status');
  }

  async adminUpload(file: File, replace: boolean): Promise<string[]> {
    const r = await this.admin<{ maps: string[] }>(`/maps?file=${encodeURIComponent(file.name)}${replace ? '&replace=1' : ''}`, { method: 'POST', body: file });
    return r.maps;
  }

  adminRebuild(map: string): Promise<unknown> {
    return this.admin(`/maps/${encodeURIComponent(map)}/rebuild`, { method: 'POST' });
  }

  adminRemove(map: string): Promise<unknown> {
    return this.admin(`/maps/${encodeURIComponent(map)}`, { method: 'DELETE' });
  }

  adminOfficial(map: string, on: boolean): Promise<unknown> {
    return this.admin(`/maps/${encodeURIComponent(map)}/official?on=${on ? 1 : 0}`, { method: 'POST' });
  }

  adminMeta(map: string, patch: { difficulty?: number | null; tags?: string[] }): Promise<unknown> {
    return this.admin(`/maps/${encodeURIComponent(map)}/meta`, { method: 'PUT', body: JSON.stringify(patch), headers: { 'Content-Type': 'application/json' } });
  }

  /** The map being played (online or offline), if any. */
  get playingMap(): string | null {
    return this.game?.map.name ?? null;
  }

  private onServerMessage = (m: ServerMessage): void => {
    switch (m.t) {
      case 'rooms':
        netState.value = { ...netState.peek(), rooms: m.rooms };
        break;
      case 'roomUpdate':
        if (netState.peek().room?.id === m.room.id) netState.value = { ...netState.peek(), room: m.room };
        break;
      case 'roomJoined':
        // joined a room, or the room's map changed (the room clock is read now)
        this.enterRoom({ ...m, receivedAt: performance.now() });
        break;
      case 'roomLeft':
        if (m.reason === 'kicked') {
          this.leaveMap(false);
          toast('You were kicked from the room', 'bad', 6000);
        } else if (m.reason === 'closed') {
          this.leaveMap(false);
          toast('The room was closed by the server', 'bad', 6000);
        }
        break;
      case 'maps': {
        // an admin changed the maps
        const client = netState.peek().client;
        if (client) client.maps = m.maps;
        netState.value = { ...netState.peek(), maps: m.maps };
        break;
      }
    }
  };

  // --- rooms ---

  private get msec(): number {
    return settings.peek().physicsMsec;
  }

  /** Runs a room request; the server answers with roomJoined (see onServerMessage). */
  private async roomRequest(what: string, request: (c: NetClient) => Promise<RoomJoined>): Promise<void> {
    const client = netState.peek().client;
    if (!client) {
      toast('Not connected to the server', 'bad');
      return;
    }
    try {
      await request(client);
    } catch (err) {
      toast(`${what}: ${(err as Error).message}`, 'bad', 6000);
    }
  }

  quickPlay(map?: string): Promise<void> {
    return this.roomRequest('Quick play', (c) => c.quickPlay(map, this.msec));
  }

  createRoom(room: CreateRoomOptions): Promise<void> {
    return this.roomRequest('Create room', (c) => c.createRoom(room, this.msec));
  }

  joinRoom(target: { id?: string; code?: string }): Promise<void> {
    return this.roomRequest('Join room', (c) => c.joinRoom(target, this.msec));
  }

  /** Solo practice: a private room of the player's own (others can still come with the code). */
  practice(map: string): Promise<void> {
    return this.createRoom({ name: `${settings.peek().playerName}'s practice`, map, maxPlayers: 16, private: true, practice: true });
  }

  setRoomMap(map: string): void {
    netState.peek().client?.setRoomMap(map);
  }

  voteMap(map: string): void {
    netState.peek().client?.voteMap(map);
  }

  vote(yes: boolean): void {
    netState.peek().client?.vote(yes);
  }

  kick(player: number): void {
    netState.peek().client?.kick(player);
  }

  /** Room loads run one after another (a map change can arrive while a map loads). */
  private roomQueue: Promise<void> = Promise.resolve();

  private enterRoom(m: RoomJoined): void {
    this.roomQueue = this.roomQueue.then(() => this.loadRoom(m));
  }

  private async loadRoom(m: RoomJoined): Promise<void> {
    const client = netState.peek().client;
    if (!client) return;
    netState.value = { ...netState.peek(), room: m.room };
    // a map change while playing: the mouse is needed for the Start button
    if (document.pointerLockElement) document.exitPointerLock();
    try {
      loading.value = { map: m.map.name, stage: 'Preparing', progress: 0 };
      phase.value = 'loading';
      await this.ensureBundle(client, m.map);
      await this.ensurePlayerAssets(client, m.map.name);
      await this.ensureSoundAssets(client, m.map.name);
      await this.loadGame(m.map.name, client, m.map, (m.serverTime ?? 0) - (m.receivedAt ?? performance.now()));
    } catch (err) {
      console.error(err);
      toast(`Could not load ${m.map.name}: ${(err as Error).message}`, 'bad', 7000);
      client.leaveRoom();
      this.leaveMap(false);
    }
  }

  // --- playing ---

  private onResize = (): void => {
    this.game?.resize(window.innerWidth, window.innerHeight);
  };

  private onPointerLockChange = (): void => {
    if (!this.game) return;
    if (!this.input.locked && phase.peek() === 'playing') this.pause();
  };

  private setLoading(map: string, stage: string): void {
    const match = LOAD_STAGES.find(([re]) => re.test(stage));
    const progress = Math.max(loading.peek()?.progress ?? 0, match ? match[1] : 0);
    loading.value = { map, stage, progress };
  }

  private readonly gameUi: GameUi = {
    runResult: (r: RunResult) => {
      runResult.value = r;
      runResultVisible.value = true;
      clearTimeout(this.resultTimer);
      this.resultTimer = window.setTimeout(() => (runResultVisible.value = false), RESULT_PANEL_MS);
    },
    dismissRunResult: () => {
      clearTimeout(this.resultTimer);
      runResultVisible.value = false;
    },
    thumbnail: (map, image) => {
      storedThumbs
        .save(map, image)
        .then(() => {
          clearLevelshotCache(map);
          mapIndexVersion.value++;
        })
        .catch((err) => console.warn('could not store thumbnail', err));
    },
    spectating: (active) => {
      spectating.value = active;
    },
    scoreboard: (open) => {
      scoreboardOpen.value = open;
    },
    players: (players) => {
      roomPlayers.value = players;
    },
  };

  /** Loads a map from its own pk3 (bundle or built-in) and starts a game on it. */
  private async loadGame(mapName: string, net: NetClient | null, info: MapInfo | null, roomClockOffset?: number): Promise<void> {
    if (this.busy) throw new Error('already loading a map');
    this.busy = true;
    try {
      const archive = this.vfs.archiveOf(`maps/${mapName}.bsp`);
      if (!archive) throw new Error(`maps/${mapName}.bsp not found`);
      // the map only sees its own pk3: textures of other maps cannot leak in
      const mapFs = VirtualFS.of([archive]);
      this.disposeGame();
      this.setLoading(mapName, 'Parsing shader scripts');
      const shaders = await loadShaders(mapFs);
      this.textures = new TextureManager(mapFs, this.anisotropy);
      const map = await loadMap(mapName, mapFs, this.textures, shaders, (t) => this.setLoading(mapName, t), this.renderer.capabilities.maxTextureSize, settings.peek().lowGraphics);
      this.setLoading(mapName, 'Starting');
      const wantThumbnail = !findLevelshot(this.vfs, mapName) && !info?.levelshot && !(await storedThumbs.load(mapName).catch(() => undefined));
      // the map's own sounds and the player sounds
      const audio = new GameAudio(this.sound, VirtualFS.of(this.soundBundle ? [this.soundBundle, archive] : [archive]), map.logic, map.world, mapName);
      audio.load().catch((err) => console.warn('sounds:', err));
      this.teamJoined = false;
      this.game = new Game(this.renderer, map, this.input, settingsStore, this.hud, this.gameUi, { net, wantThumbnail, players: this.players, audio, roomClockOffset });
      this.game.resize(window.innerWidth, window.innerHeight);
      runResult.value = null;
      runResultVisible.value = false;
      gameState.value = { map: mapName, online: !!net, serverName: net?.serverName ?? null, info, runs: [...map.logic.runs.keys()], rules: { ...map.logic.rules }, teams: [...map.logic.teams] };
      const s = settings.peek();
      updateSettings({ recentMaps: pushRecent(s.recentMaps, { name: mapName, at: Date.now() }, (a, b) => a.name === b.name) });
      loading.value = { map: mapName, stage: 'Ready', progress: 1 };
      phase.value = 'loading';
    } finally {
      this.busy = false;
    }
  }

  /** Plays a map on this device without the server. */
  async playOffline(mapName: string): Promise<void> {
    try {
      loading.value = { map: mapName, stage: 'Preparing', progress: 0 };
      phase.value = 'loading';
      await this.cachedPlayerAssets();
      await this.cachedSoundAssets();
      await this.loadGame(mapName, null, null);
    } catch (err) {
      console.error(err);
      toast(`Could not load ${mapName}: ${(err as Error).message}`, 'bad', 7000);
      loading.value = null;
      phase.value = this.game ? 'paused' : 'menu';
    }
  }

  /** The player's team on the loaded map (null without a map). */
  get team(): Team | null {
    return this.game?.map.session.team ?? null;
  }

  /**
   * Starts playing the map just loaded as a member of the team (default: the
   * team picked last, if the map has spawns for it).
   */
  play(team?: Team): void {
    if (!this.game) return;
    const teams = this.game.map.logic.teams;
    const last = settings.peek().team;
    const t = team !== undefined && teams.includes(team) ? team : teams.includes(last) ? last : teams[0];
    this.game.setTeam(t);
    if (teams.length > 1) toast(`You play as ${TEAM_NAMES[t]}`, 'info');
    this.teamJoined = true;
    this.resume();
  }

  /** Joins the other team (from the pause menu): respawn at its spawn and play on. */
  changeTeam(team: Team): void {
    if (!this.game?.map.logic.teams.includes(team)) return;
    this.game.stopFollowing();
    this.game.setTeam(team);
    toast(`You joined the ${TEAM_NAMES[team]}`, 'info');
    this.resume();
  }

  /** Starts (or continues) playing: captures the mouse and runs the game loop. */
  resume(): void {
    if (!this.game) return;
    // the first start on a map joins a team (the server is told, so both simulate the same player)
    if (!this.teamJoined) return this.play();
    loading.value = null;
    pauseView.value = 'main';
    phase.value = 'playing';
    (document.activeElement as HTMLElement | null)?.blur?.();
    // a user gesture: the browser lets audio start now
    this.sound.unlock();
    this.sound.setPaused(false);
    this.input.lock();
    this.game.start();
    // without the mouse captured the game is not playable: back to the pause menu
    setTimeout(() => {
      if (phase.peek() === 'playing' && !this.input.locked) {
        this.pause();
        toast('Click Resume (or the game view) to capture the mouse', 'info');
      }
    }, LOCK_TIMEOUT_MS);
  }

  pause(): void {
    if (!this.game) return;
    this.game.stop();
    this.sound.setPaused(true);
    this.game.map.session.interrupt('paused');
    this.pausedAt = performance.now();
    pauseView.value = 'main';
    scoreboardOpen.value = false;
    phase.value = 'paused';
  }

  /** True right after the pointer lock was released by Escape (that key press must not also resume). */
  get justPaused(): boolean {
    return performance.now() - this.pausedAt < 400;
  }

  restartRun(): void {
    if (!this.game) return;
    if (this.game.following) this.game.stopFollowing();
    this.game.perform('respawn');
    runResultVisible.value = false;
    if (phase.peek() !== 'playing') this.resume();
  }

  /** Spectates a player of the room (from the menus): back into the game, watching them. */
  spectate(player: number | 'next'): void {
    if (!this.game?.net) return;
    this.game.spectatePlayer(player);
    if (phase.peek() !== 'playing') this.resume();
  }

  /** Stops spectating and plays again (from the spawn). */
  stopSpectating(): void {
    this.game?.stopFollowing();
  }

  watchReplay(): void {
    if (!this.game?.lastRun?.replay) return;
    if (phase.peek() !== 'playing') this.resume();
    this.game.watchLastRun();
  }

  private disposeGame(): void {
    this.game?.dispose();
    this.game = null;
    this.textures?.dispose();
    this.textures = null;
  }

  /** Back to the menu; leaves the room on the server unless told otherwise (it already left us). */
  leaveMap(tellServer = true): void {
    if (tellServer && (this.game?.net || netState.peek().room)) netState.peek().client?.leaveRoom();
    this.disposeGame();
    netState.value = { ...netState.peek(), room: null };
    gameState.value = null;
    runResult.value = null;
    runResultVisible.value = false;
    spectating.value = false;
    scoreboardOpen.value = false;
    roomPlayers.value = [];
    loading.value = null;
    this.hud.setVisible(false);
    this.renderer.clear();
    if (document.pointerLockElement) document.exitPointerLock();
    phase.value = 'menu';
  }

  // --- navigation helpers for the UI ---

  openMap(name: string): void {
    selectedMap.value = name;
    tab.value = 'maps';
  }
}
